import React, { Component } from 'react';
import styled, { css } from 'styled-components';

import i18n, { translations } from 'i18n';
import { Link } from 'components/Common';
import ImageContainer from 'components/ImageContainer';
import circleCheck from 'inline-icons/circle-check.svg';
import notInstalledArrow from 'inline-icons/not-installed-arrow.svg';
import emptySlotIonic from 'inline-icons/empty-slot-ionic.svg';
import emptySlotVersa from 'inline-icons/empty-slot-versa.svg';
import emptySlotCharge3 from 'inline-icons/empty-slot-charge_3.svg';
import emptySlotInspire from 'inline-icons/empty-slot-inspire.svg';
import emptySlotSense from 'inline-icons/empty-slot-sense.svg';
import emptySlotMorgan from 'inline-icons/empty-slot-morgan.svg';

import Modal from './Modal';

const DEVICES = {
  ionic: {
    emptySlot: emptySlotIonic
  },
  versa: {
    emptySlot: emptySlotVersa
  },
  'versa lite': {
    emptySlot: emptySlotVersa
  },
  'versa 2': {
    emptySlot: emptySlotVersa
  },
  'versa 3': {
    emptySlot: emptySlotSense
  },
  'versa 4': {
    emptySlot: emptySlotSense
  },
  'charge 3': {
    emptySlot: emptySlotCharge3
  },
  'charge 4': {
    emptySlot: emptySlotCharge3
  },
  sense: {
    emptySlot: emptySlotSense
  },
  'sense 2': {
    emptySlot: emptySlotSense
  },
  inspire: {
    emptySlot: emptySlotInspire
  },
  'charge 5': {
    emptySlot: emptySlotMorgan
  }
};

export const SelectedClocks = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;

  box-sizing: border-box;

  padding: 15px 20px 20px;

  background-color: #ffffff;

  box-shadow: 0 0 64px 0 rgba(17, 37, 48, 0.05);

  overflow: hidden;

  z-index: 3;
`;

export const SelectedHeader = styled.div`
  display: flex;
  font-size: 17px;

  margin-bottom: 10px;
`;

export const SelectedText = styled.span`
  flex: 1;
  font-weight: 500;
  color: #112538;
`;

export const HelpInfo = styled.span`
  color: #00b0b9;
`;

const sizeMixin = ({ image: { width: originalWidth, height = 54 } = {} }) => {
  const width = 54;
  height /= originalWidth / 54;

  return css`
    width: ${width}px;
    height: ${height}px;

    @media (max-width: 320px) {
      width: ${width - width * 0.1}px;
      height: ${height - height * 0.1}px;
    }
  `;
};

const ClockImage = styled(({ image, name, ...props }) => (
  <ImageContainer
    {...props}
    containerAttr={{
      width: image.width,
      height: image.height
    }}
    imageAttr={{
      alt: name,
      src: image.uri
    }}
  />
))`
  ${sizeMixin}

  overflow: hidden;
`;

const BaseContainer = styled.div`
  position: relative;
`;

const Icon = styled.div`
  box-sizing: border-box;
  position: absolute;
  top: -6px;
  right: -6px;
  width: 17px;
  height: 17px;
`;

const ActiveIcon = styled(Icon).attrs({
  dangerouslySetInnerHTML: { __html: circleCheck }
})``;

const NotInstalledIcon = styled(Icon).attrs({
  dangerouslySetInnerHTML: { __html: notInstalledArrow }
})``;

export const Clock = ({
  clock: {
    id,
    banners: [image],
    name,
    isOnDevice
  },
  active,
  setFocusedClock,
  ...props
}) => (
  <BaseContainer>
    {active && <ActiveIcon />}
    {!active && !isOnDevice && <NotInstalledIcon />}
    <ClockImage
      image={image}
      name={name}
      onClick={() => setFocusedClock(id)}
      {...props}
    />
  </BaseContainer>
);

const EmptySlotIcon = ({ deviceIcon, ...props }) => (
  <div dangerouslySetInnerHTML={{ __html: deviceIcon }} {...props} />
);

const PlaceholderIcon = styled(EmptySlotIcon)`
  position: relative;
  box-sizing: border-box;

  ${sizeMixin}
`;

export const Placeholder = ({ clock, device, index }) => {
  const image = clock && clock.banners[0];

  const deviceIcon = device && device.emptySlot;

  return (
    <BaseContainer>
      <Link to="/clocks/gallery" id={`select-clock-${index}`}>
        <PlaceholderIcon image={image} deviceIcon={deviceIcon} />
      </Link>
    </BaseContainer>
  );
};

export const ClocksRow = styled.div`
  display: flex;
  justify-content: space-between;

  overflow: hidden;

  padding-top: 10px;

  // This is to ensure the not installed icon does not get cut off
  margin-right: -6px;
  padding-right: 6px;
`;

const createPlaceholders = ({ placeholderCount, deviceName, clock }) => {
  const placeholders = [];
  const device = DEVICES[deviceName];

  for (let i = 0; i < placeholderCount; i += 1) {
    placeholders.push(
      <Placeholder index={i} key={i} device={device} clock={clock} />
    );
  }

  return placeholders;
};

class HelpModal extends Component {
  state = {
    open: false
  };

  onOpen = () =>
    this.setState({
      open: true
    });

  onClose = () =>
    this.setState({
      open: false
    });

  render() {
    const { maxFavoriteClocks } = this.props;

    const {
      page: {
        ClockGallery: {
          MyClocks: { help }
        }
      }
    } = translations;

    return (
      <div>
        <HelpInfo onClick={this.onOpen}>{help.button()}</HelpInfo>
        {this.state.open && (
          <Modal
            title={help.modal.title()}
            content={help.modal.content({ maxFavoriteClocks })}
            buttons={[
              {
                text: help.modal.button(),
                onClick: this.onClose
              }
            ]}
            onClose={this.onClose}
          />
        )}
      </div>
    );
  }
}

export default ({
  favoriteClocks = [],
  maxFavoriteClocks,
  deviceName,
  activeClock,
  setFocusedClock
}) => (
  <SelectedClocks>
    <SelectedHeader>
      <SelectedText>
        {i18n('page.ClockGallery.MyClocks.selectedTitle')()}
      </SelectedText>
      <HelpModal maxFavoriteClocks={maxFavoriteClocks} />
    </SelectedHeader>
    <ClocksRow>
      {[...favoriteClocks]
        .sort(({ id }) => (id === activeClock.id ? -1 : 1))
        .map((app) => (
          <Clock
            key={app.id}
            clock={app}
            deviceName={deviceName}
            active={activeClock.id === app.id}
            setFocusedClock={setFocusedClock}
          />
        ))}
      {DEVICES[deviceName] &&
        createPlaceholders({
          placeholderCount: maxFavoriteClocks - favoriteClocks.length,
          deviceName,
          clock: favoriteClocks[0]
        })}
    </ClocksRow>
  </SelectedClocks>
);
